import { useEffect, useState } from 'react';
import history from '@history';
import { useLocation } from 'react-router-dom';
import { Box, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const numInPathExp = /\d+/;

/**
 * ComposedBreadcrumb
 *
 * Componente para mostrar breadcrumbs de navegación, con opción de regresar y redirección.
 *
 * @component
 * @param {function} [redirect] - Callback para manejar el botón de regreso
 * @param {boolean} [disableRoute=false] - Si se oculta el breadcrumb
 * @param {boolean} [enableRedirect=false] - Si permite redirección al hacer click en los breadcrumbs
 * @returns {JSX.Element}
 */
export const ComposedBreadcrumb = ({ redirect, disableRoute = false, enableRedirect = false }) => {
  const [locations, setLocations] = useState([]);
  const { pathname } = useLocation();

  const handleGetPaths = (currentPath) => {
    const parts = currentPath.split('/').filter((part) => part !== '');
    const result = parts.reduce((acumulator, part, index) => {
      const hasNumbers = numInPathExp.test(part);
      if (!hasNumbers) {
        const name = part;
        const path = `/${parts.slice(0, index + 1).join('/')}`;
        acumulator.push({ name, path });
      }
      return acumulator;
    }, []);
    return result;
  };

  const handleLink = (path = '') => {
    if (!enableRedirect) return;
    history.push(path);
  };

  useEffect(() => {
    if (!pathname) {
      return;
    }
    const objectPath = handleGetPaths(pathname);
    setLocations(objectPath);
  }, [pathname]);

  const handleBack = (e) => {
    if (redirect !== undefined && typeof redirect === 'function') {
      redirect(e);
      return;
    }
    e.preventDefault();
    history.back();
  };

  return (
    <div role="presentation" className="flex flex-row flexwrap items-center">
      <Tooltip title="Regresar a página anterior">
        <IconButton aria-label="delete" size="small" onClick={handleBack}>
          <ArrowBackIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      {!disableRoute && (
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{
            li: {
              marginLeft: '0px',
              marginRight: '0px',
            },
          }}
        >
          {locations.map((location, index) => (
            <Box
              className="p-0"
              key={`location_${index}`}
              size="small"
              onClick={() => handleLink(location.path)}
              variant="text"
              disabled={index === locations.length - 1 || !enableRedirect}
              sx={{ fontSize: '1.6rem' }}
            >
              {location.name}
            </Box>
          ))}
        </Breadcrumbs>
      )}
    </div>
  );
};
